import React from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import { toast } from "react-toastify";
import type {
  SpecType,
  SpecsValidationIssue,
} from "../../../service/SpecsRevisionService";
import { createStyles, palette, spacing } from "../../../styles/theme";
import type { ObservableModelRoot } from "../specsModel/observable";
import { getSpecsIssueMatchForModelPath } from "../specsValidationIssues";
import {
  appendItemAtModelPath,
  getValueAtModelPath,
  joinModelPath,
  removeItemAtModelPath,
  setValueAtModelPath,
} from "./modelPath";
import SpecsFormEntriesGrid from "./SpecsFormEntriesGrid";
import {
  getValidationIssueFrameSx,
  SpecsValidationIssueHint,
} from "./SpecsValidationIssueHighlight";
import type {
  SpecsFormArrayClipboardSchema,
  SpecsFormArraySchema,
  SpecsFormEntry,
} from "./types";

const styles = createStyles({
  nestedPaper: {
    p: spacing.groupingDesktop,
    bgcolor: palette.grey.lightBackground,
    border: `1px solid ${palette.grey.lightGrey}`,
  },
  itemPaper: {
    p: spacing.groupingDesktop,
    border: `1px solid ${palette.grey.lightGrey}`,
  },
});

type SpecsArraySectionProps = {
  model: ObservableModelRoot;
  entry: SpecsFormArraySchema;
  basePath: string;
  validationIssues?: SpecsValidationIssue[];
  validationSpecType?: SpecType;
  renderEntry: (entry: SpecsFormEntry, basePath: string) => React.ReactNode;
};

function SpecsArrayClipboardActions({
  arrayPath,
  entry,
  model,
  resolvedItems,
}: {
  arrayPath: string;
  entry: SpecsFormArrayClipboardSchema;
  model: ObservableModelRoot;
  resolvedItems: unknown[];
}) {
  const copyItems = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(entry.serializeClipboardItems(resolvedItems), null, 2)
      );
      toast.success(`Copied ${entry.clipboardLabel} to clipboard`);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : `Failed to copy ${entry.clipboardLabel} to clipboard`
      );
    }
  }, [entry, resolvedItems]);
  const pasteItems = React.useCallback(async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      const parsed = JSON.parse(clipboardText);

      if (!Array.isArray(parsed)) {
        throw new Error(`Clipboard does not contain ${entry.clipboardLabel}`);
      }

      const nextItems = entry.restoreClipboardItems(parsed);
      setValueAtModelPath(model, arrayPath, nextItems);
      toast.success(`Pasted ${entry.clipboardLabel} from clipboard`);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : `Failed to paste ${entry.clipboardLabel} from clipboard`
      );
    }
  }, [arrayPath, entry, model]);

  return (
    <Stack direction="row" spacing={0.5}>
      <Tooltip title={`Copy ${entry.title}`}>
        <span>
          <IconButton
            size="small"
            onClick={copyItems}
            disabled={resolvedItems.length === 0}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={`Paste ${entry.title}`}>
        <span>
          <IconButton size="small" onClick={pasteItems}>
            <ContentPasteIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}

export default function SpecsArraySection({
  model,
  entry,
  basePath,
  validationIssues = [],
  validationSpecType,
  renderEntry,
}: SpecsArraySectionProps) {
  const arrayPath = joinModelPath(basePath, entry.path);
  const items = getValueAtModelPath(model, arrayPath);
  const resolvedItems = Array.isArray(items) ? items : [];
  const sectionIssueMatch = validationSpecType
    ? getSpecsIssueMatchForModelPath({
        issues: validationIssues,
        specType: validationSpecType,
        modelPath: arrayPath,
        includeAncestorTargets: true,
      })
    : null;
  const addButton = (
    <Button
      variant="outlined"
      size="small"
      onClick={() =>
        appendItemAtModelPath(model, arrayPath, entry.createItem())
      }
    >
      {entry.addButtonLabel ?? "Ajouter"}
    </Button>
  );
  const content = (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <div>
          <Typography variant="subtitle2">{entry.title}</Typography>
          {entry.description ? (
            <Typography variant="body2" color="text.secondary">
              {entry.description}
            </Typography>
          ) : null}
        </div>
        <Box sx={{ flex: 1 }} />
        {entry.enableClipboard ? (
          <SpecsArrayClipboardActions
            arrayPath={arrayPath}
            entry={entry}
            model={model}
            resolvedItems={resolvedItems}
          />
        ) : null}
      </Stack>
      <SpecsValidationIssueHint match={sectionIssueMatch} />

      {resolvedItems.length === 0 ? (
        <Alert severity="info">No entries yet.</Alert>
      ) : (
        <Stack spacing={2}>
          {resolvedItems.map((item, index) => {
            const itemPath = joinModelPath(arrayPath, String(index));
            const itemLabel = entry.getItemLabel
              ? entry.getItemLabel(item, index)
              : `${entry.title} ${index + 1}`;
            const itemIssueMatch = validationSpecType
              ? getSpecsIssueMatchForModelPath({
                  issues: validationIssues,
                  specType: validationSpecType,
                  modelPath: itemPath,
                  includeAncestorTargets: true,
                })
              : null;

            return (
              <Paper
                key={itemPath}
                variant="outlined"
                sx={{
                  ...styles.itemPaper,
                  ...getValidationIssueFrameSx(itemIssueMatch),
                }}
              >
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">{itemLabel}</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Button
                      variant="outlined"
                      size="small"
                      color="inherit"
                      onClick={() =>
                        removeItemAtModelPath(model, arrayPath, index)
                      }
                    >
                      Supprimer
                    </Button>
                  </Stack>
                  <SpecsValidationIssueHint match={itemIssueMatch} />
                  <SpecsFormEntriesGrid
                    entries={entry.entries}
                    basePath={itemPath}
                    columns={entry.columns}
                    renderEntry={(childEntry) =>
                      renderEntry(childEntry, itemPath)
                    }
                  />
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        {addButton}
      </Box>
    </Stack>
  );

  if (entry.unframed) {
    return content;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        ...styles.nestedPaper,
        ...getValidationIssueFrameSx(sectionIssueMatch),
      }}
    >
      {content}
    </Paper>
  );
}
